import { Link } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  ArrowLeft, 
  Shield, 
  Database, 
  Eye, 
  Cookie, 
  Share2, 
  UserCog, 
  Lock, 
  RefreshCw,
} from 'lucide-react'

export function Privacy() {
  useDocumentHead({
    title: 'Privacy Policy',
    description: 'Privacy policy for Badminton Leagues. Learn how we handle your data, what we collect, and your rights.',
  })

  const sections = [
    {
      icon: Database,
      title: 'What We Collect',
      content: (
        <ul className="space-y-1 text-muted-foreground text-sm">
          <li>• Account details (email, name, profile picture)</li>
          <li>• Content you create (groups, matches, results)</li>
          <li>• Technical information needed to operate</li>
        </ul>
      ),
    },
    {
      icon: Eye,
      title: 'How We Use It',
      content: (
        <ul className="space-y-1 text-muted-foreground text-sm">
          <li>• Provide and operate the service</li>
          <li>• Display your profile to other users</li>
          <li>• Improve the service</li>
        </ul>
      ),
    },
    {
      icon: Cookie,
      title: 'Cookies',
      content: (
        <p className="text-muted-foreground">
          We use essential cookies only for login and basic functionality. 
          No tracking or advertising.
        </p>
      ),
    },
    {
      icon: Share2,
      title: 'Third Parties',
      content: (
        <p className="text-muted-foreground">
          We use third-party services to operate (hosting, authentication). 
          We don't sell your data.
        </p>
      ),
    },
    {
      icon: UserCog,
      title: 'Visibility',
      content: (
        <p className="text-muted-foreground">
          Your profile and activity are visible to members of groups you join. 
          We may share data if required by law.
        </p>
      ),
    },
    {
      icon: Shield,
      title: 'Your Rights',
      content: (
        <p className="text-muted-foreground">
          You can update your profile anytime. If you want your data deleted, 
          let us know and we'll handle it.
        </p>
      ),
    },
    {
      icon: Lock,
      title: 'Security',
      content: (
        <p className="text-muted-foreground">
          We take reasonable measures to protect your data, but no system is 
          completely secure.
        </p>
      ),
    },
    {
      icon: RefreshCw,
      title: 'Changes',
      content: (
        <p className="text-muted-foreground">
          We may update this policy. Continued use means you accept any changes.
        </p>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl flex-1">
        <Button variant="ghost" size="sm" asChild className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </Button>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground">
            Last updated: December 2024
          </p>
        </div>

        {/* Intro */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <p className="text-center">
              Badminton Leagues is a hobby project by an individual. This explains how we handle your data. 
              By using the service, you agree to these practices.
            </p>
          </CardContent>
        </Card>

        {/* Sections Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {sections.map((section) => (
            <Card key={section.title} className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <section.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {section.content}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}
